/**
 * Review the proof a party sent for an action (docs/inspections-platform.md,
 * section 10): approve it, which closes the action, or send it back with the
 * reason, which asks its owner for proof again through a new link.
 *
 * The reason is required to send proof back, because it is what the party
 * reads in the new link's email. The backend enforces it too.
 */

import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { PunchListItem } from '@/admin/hooks/use-inspections'

export type ProofDecision = 'approve' | 'reject'

export function ReviewProofDialog({
  item,
  decision,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  item: PunchListItem | null
  decision: ProofDecision
  onOpenChange: (open: boolean) => void
  onSubmit: (notes: string) => void | Promise<void>
  isPending: boolean
}) {
  const { t } = useTranslation()
  const reasonRequired = t('inspections.proofReview.reasonRequired', 'Say what needs to change.')
  const approving = decision === 'approve'

  const fields: FormField[] = [
    {
      type: 'textarea',
      name: 'notes',
      label: approving
        ? t('inspections.proofReview.noteLabel', 'Note (optional)')
        : t('inspections.proofReview.reasonLabel', 'What needs to change'),
      value: '',
      placeholder: approving
        ? t('inspections.proofReview.notePlaceholder', 'Anything to record about the repair.')
        : t(
            'inspections.proofReview.reasonPlaceholder',
            'e.g. The photo is too dark to see the flashing.',
          ),
      ...(approving
        ? { validationSchema: z.string().optional() }
        : {
            required: reasonRequired,
            validationSchema: z.string().trim().min(1, reasonRequired),
          }),
    },
  ]

  const { Form, FormFooter } = useForm(
    { name: 'review-proof', submit: 'footer', fields },
    async (values) => {
      await onSubmit(String((values as { notes?: string }).notes ?? ''))
    },
    [item?.uuid, decision, reasonRequired],
  )

  if (!item) return null

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {approving
              ? t('inspections.proofReview.approveTitle', 'Approve this repair?')
              : t('inspections.proofReview.rejectTitle', 'Send this repair back?')}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line">{item.description}</DialogDescription>
        </DialogHeader>

        <p className="text-[12px] text-[var(--ink-3)]">
          {approving
            ? t('inspections.proofReview.approveHint', 'The action closes, with the photos as its proof.')
            : t(
                'inspections.proofReview.rejectHint',
                'The photos stop counting as proof, and a new link goes out that says why.',
              )}
        </p>

        <Form className="flex flex-col gap-4 py-1" />

        {FormFooter && (
          <FormFooter
            onCancel={() => onOpenChange(false)}
            // Stays enabled on an empty reason - clicking validates and
            // highlights it, per the house rule.
            submitText={
              isPending
                ? t('inspections.proofReview.saving', 'Saving…')
                : approving
                  ? t('inspections.proofReview.approve', 'Approve')
                  : t('inspections.proofReview.sendBack', 'Send back')
            }
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
